import classNames from 'clsx';
import { ReactComponent as AndroidLogoIcon } from 'img/icons/Android_logo.svg';
import { ReactComponent as AppleLogoIcon } from 'img/icons/Apple_logo.svg';
import { ReactComponent as HuaweiLogoIcon } from 'img/icons/huawei_logo.svg';
import React from 'react';
import { useSelector } from 'react-redux';
import { QuotaLandingSelectors } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './StoreButton.css';

export enum EStoreType {
    IOS = 'IOS',
    ANDROID = 'ANDROID',
    RUSTORE = 'RUSTORE',
    HUAWEI = 'HUAWEI',
}

interface IProps {
    mobileType: EStoreType;
    gaCategory?: string;
    title?: string;
    fullWidth?: boolean;
    appStoreLink?: string;
    googlePlayLink?: string;
    ruStoreLink?: string;
    appGalleryLink?: string;
    onClick?: (mobileType) => void;
    isRebrandingQuotaLanding?: boolean;
}

export default function StoreButton({
    mobileType,
    gaCategory = 'mobile',
    title,
    fullWidth = false,
    appStoreLink,
    googlePlayLink,
    ruStoreLink,
    appGalleryLink,
    onClick = noopVoid,
    isRebrandingQuotaLanding = false,
}: IProps) {
    const isMobile = useSelector(QuotaLandingSelectors.isMobile);
    const isIFrame = useSelector(QuotaLandingSelectors.isIFrame);

    const getIcon = (mobileType, isHuawei) => {
        if (isHuawei) {
            return <HuaweiLogoIcon />;
        }

        if (mobileType !== EStoreType.ANDROID) {
            return <AppleLogoIcon />;
        }

        return <AndroidLogoIcon />;
    };

    const getIconWithText = (title, mobileType, isHuawei = false) => {
        return title ? (
            <div className={styles.inner}>
                {getIcon(mobileType, isHuawei)}
                <span>{title}</span>
            </div>
        ) : null;
    };

    return (
        <div
            className={classNames({
                [styles.mobileStores]: true,
                [styles.mobileStoresMobile]: isMobile && !isIFrame,
                [styles.fullWidth]: fullWidth,
                [styles.small]: !!title,
                [styles.rebrandingQuotaLanding]: isRebrandingQuotaLanding,
            })}
        >
            {mobileType !== EStoreType.IOS && ruStoreLink && (
                <a
                    href={ruStoreLink}
                    className={classNames({
                        [styles.mobileStore]: true,
                        [styles.mobileStoreRustore]: true,
                        [styles.mobileStoreMobile]: isMobile,
                    })}
                    data-category={gaCategory}
                    data-action="rustore-click"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onClick(EStoreType.RUSTORE)}
                >
                    {getIconWithText(title, EStoreType.RUSTORE)}
                </a>
            )}
            {mobileType !== EStoreType.ANDROID && (
                <a
                    href={appStoreLink || 'https://trk.mail.ru/c/g1bqp3?mt_idfa={mt_idfa}'}
                    className={classNames({
                        [styles.mobileStore]: true,
                        [styles.mobileStoreApple]: true,
                        [styles.mobileStoreMobile]: isMobile,
                    })}
                    data-category={gaCategory}
                    data-action="applestore-click"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onClick(EStoreType.IOS)}
                >
                    {getIconWithText(title, mobileType)}
                </a>
            )}
            {mobileType !== EStoreType.IOS && (
                <a
                    href={googlePlayLink || 'https://trk.mail.ru/c/iq5vm4?mt_gaid={mt_gaid}'}
                    className={classNames({
                        [styles.mobileStore]: true,
                        [styles.mobileStoreGoogle]: true,
                        [styles.mobileStoreMobile]: isMobile,
                    })}
                    data-category={gaCategory}
                    data-action="googlestore-click"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onClick(EStoreType.ANDROID)}
                >
                    {getIconWithText(title, mobileType)}
                </a>
            )}
            {(!mobileType || (mobileType !== EStoreType.IOS && appGalleryLink)) && appGalleryLink && (
                <a
                    href={appGalleryLink}
                    className={classNames({
                        [styles.mobileStore]: true,
                        [styles.mobileStoreHuawei]: true,
                        [styles.mobileStoreMobile]: isMobile,
                    })}
                    data-category={gaCategory}
                    data-action="huaweistore-click"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => onClick(EStoreType.HUAWEI)}
                >
                    {getIconWithText(title, mobileType, !!appGalleryLink)}
                </a>
            )}
        </div>
    );
}
